import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import FlipToRevealCard from '../components/FlipToRevealCard';
import { saveUserEntry, getSettings, validateSerialNumber, markSerialNumberUsed } from '../utils/db';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import ConfettiEffect from '../components/ConfettiEffect';

const { FiUser, FiPhone, FiCheckCircle, FiCopy, FiAlertTriangle, FiFileText, FiKey } = FiIcons;

const FlipRevealFlow = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({ name: '', mobile: '', receiptNumber: '', serialNumber: '' });
  const [reward, setReward] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  
  const [settings, setSettings] = useState(getSettings());

  useEffect(() => {
    const handleSettings = () => setSettings(getSettings());
    handleSettings();
    window.addEventListener('settingsUpdated', handleSettings);
    window.addEventListener('storage', handleSettings);
    return () => {
      window.removeEventListener('settingsUpdated', handleSettings);
      window.removeEventListener('storage', handleSettings);
    };
  }, []);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const generateCoupon = () => {
    return `FLIP-${new Date().getFullYear()}-${Math.floor(100000 + Math.random() * 900000)}`;
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.mobile || !formData.receiptNumber) return;

    // Mobile validation (10-digit Indian numbers with optional country code)
    const phoneClean = formData.mobile.replace(/[^0-9+]/g, '');
    const phoneRegex = /^(?:\+?\d{1,4})?[6-9]\d{9}$/;
    if (!phoneRegex.test(phoneClean)) {
      return setError('Please enter a valid 10-digit mobile number.');
    }

    if (settings.requireSerialNumber) {
      if (!formData.serialNumber) {
        return setError('Token / Serial Number is required to participate.');
      }
      if (!validateSerialNumber(formData.serialNumber)) {
        return setError('Invalid or already used Token / Serial Number.');
      }
    }

    setIsSubmitting(true);

    setTimeout(() => {
      if (settings.requireSerialNumber) {
        markSerialNumberUsed(formData.serialNumber);
      }

      const code = generateCoupon();
      const availablePrizes = settings.prizes?.length > 0 ? settings.prizes : ["Mystery Prize"];
      const randomReward = availablePrizes[Math.floor(Math.random() * availablePrizes.length)];
      
      setCouponCode(code);
      setReward(randomReward);
      setStep(2);
      setIsSubmitting(false);
    }, 800);
  };

  const handleRewardRevealed = () => {
    saveUserEntry({ ...formData, couponCode, reward, gameType: 'Flip Card' });
    setStep(3);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(couponCode);
    alert('Coupon code copied!');
  };

  const resetFlow = () => {
    setStep(1);
    setFormData({ name: '', mobile: '', receiptNumber: '', serialNumber: '' });
    setReward('');
    setCouponCode('');
    setError('');
  };

  if (settings?.campaignStatus === 'paused') {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6 text-center">
        <div className="glass-panel p-8 rounded-3xl max-w-md w-full border-amber-500/30">
          <SafeIcon icon={FiAlertTriangle} className="text-5xl text-amber-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Campaign Paused</h2>
          <p className="text-slate-400 mb-6">The organizer has temporarily paused this campaign. Please check back shortly.</p>
        </div>
      </div>
    );
  }

  if (settings.activeGame !== 'flip') {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6 text-center">
        <div className="glass-panel p-8 rounded-3xl max-w-md w-full">
          <SafeIcon icon={FiAlertTriangle} className="text-5xl text-emerald-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Campaign Inactive</h2>
          <p className="text-slate-400 mb-6">This game mode is currently not active. Please look at the main display screen for the active campaign.</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen bg-cover bg-center bg-fixed bg-no-repeat relative flex flex-col items-center justify-center p-4"
      style={{ backgroundImage: `url('${settings.bgImage}')` }}
    >
      <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm z-0"></div>
      
      {settings.brandLogo && (
        <div className="absolute top-6 left-1/2 -translate-x-1/2 z-50 pointer-events-none">
          <img src={settings.brandLogo} alt="Logo" className="h-10 md:h-14 object-contain drop-shadow-lg" />
        </div>
      )}

      {step === 3 && !reward.includes('Luck') && !reward.includes('Try') && <ConfettiEffect />}

      <div className="relative z-10 w-full max-w-md mt-16 md:mt-0">
        <div className="flex items-center justify-center gap-3 mb-8 relative z-10">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-500 ${step >= 1 ? 'bg-emerald-500 text-white shadow-[0_0_15px_rgba(16,185,129,0.5)]' : 'bg-white/10 text-white/40'}`}>1</div>
          <div className={`w-12 h-1 rounded-full transition-all duration-500 ${step >= 2 ? 'bg-emerald-500' : 'bg-white/10'}`}></div>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-500 ${step >= 2 ? 'bg-emerald-500 text-white shadow-[0_0_15px_rgba(16,185,129,0.5)]' : 'bg-white/10 text-white/40'}`}>2</div>
          <div className={`w-12 h-1 rounded-full transition-all duration-500 ${step >= 3 ? 'bg-emerald-500' : 'bg-white/10'}`}></div>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-500 ${step >= 3 ? 'bg-emerald-500 text-white shadow-[0_0_15px_rgba(16,185,129,0.5)]' : 'bg-white/10 text-white/40'}`}>3</div>
        </div>

        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div key="step1" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.4 }} className="glass-panel rounded-3xl p-8">
              <div className="text-center mb-8">
                <h1 className="text-3xl font-extrabold text-slate-900 dark:text-white mb-2">Tap & Flip Card!</h1>
                <p className="text-slate-600 dark:text-slate-300 text-sm">Enter your details to reveal your secret mystery card!</p>
              </div>

              <form onSubmit={handleFormSubmit} className="space-y-5">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <SafeIcon icon={FiUser} className="text-slate-400" />
                  </div>
                  <input autoFocus type="text" name="name" required placeholder="Full Name" value={formData.name} onChange={handleInputChange} className="w-full pl-10 pr-4 py-3 bg-slate-100/50 dark:bg-white/5 border border-slate-300 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 outline-none transition-all" />
                </div>
                
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <SafeIcon icon={FiPhone} className="text-slate-400" />
                  </div>
                  <input type="tel" name="mobile" required placeholder="Mobile Number" value={formData.mobile} onChange={handleInputChange} className="w-full pl-10 pr-4 py-3 bg-slate-100/50 dark:bg-white/5 border border-slate-300 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 outline-none transition-all" />
                </div>

                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <SafeIcon icon={FiFileText} className="text-slate-400" />
                  </div>
                  <input type="text" name="receiptNumber" required placeholder="Receipt / Ticket Number" value={formData.receiptNumber} onChange={handleInputChange} className="w-full pl-10 pr-4 py-3 bg-slate-100/50 dark:bg-white/5 border border-slate-300 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 outline-none transition-all" />
                </div>

                {settings.requireSerialNumber && (
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <SafeIcon icon={FiKey} className="text-amber-400" />
                    </div>
                    <input type="text" name="serialNumber" required placeholder="Token / Serial Number (e.g. EVENT-001)" value={formData.serialNumber} onChange={handleInputChange} className="w-full pl-10 pr-4 py-3 bg-white/5 border border-amber-500/50 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent text-white placeholder-amber-400/50 outline-none transition-all uppercase" />
                  </div>
                )}

                {error && (
                  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="text-red-400 text-sm text-center font-medium bg-red-500/10 py-2 rounded-lg border border-red-500/20">
                    {error}
                  </motion.div>
                )}

                <button type="submit" disabled={isSubmitting} className="w-full py-4 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-bold text-lg shadow-lg transform active:scale-95 transition-all flex justify-center items-center gap-2 mt-2">
                  {isSubmitting ? <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : "Get My Mystery Card"}
                </button>
              </form>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div key="step2" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.5 }} className="flex flex-col items-center">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-white mb-2">Claim Your Prize</h2>
                <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full border border-white/20 backdrop-blur-md">
                  <span className="text-slate-300 text-sm">Coupon:</span>
                  <span className="text-white font-mono font-bold tracking-wider">{couponCode}</span>
                </div>
              </div>
              <FlipToRevealCard reward={reward} onRevealed={handleRewardRevealed} />
              <p className="text-slate-400 text-sm mt-6 animate-pulse">Tap the card above to flip and reveal</p>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div key="step3" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-panel rounded-3xl p-8 text-center flex flex-col items-center w-full">
              <div className="flex-1 w-full">
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", damping: 12, delay: 0.2 }} className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6 relative z-10">
                  <SafeIcon icon={FiCheckCircle} className="text-4xl text-green-400" />
                </motion.div>
                <h2 className="text-3xl font-bold text-white mb-2 relative z-10">
                  {(reward.includes('Luck') || reward.includes('Try')) ? 'Aww Snap!' : 'Entry Confirmed!'}
                </h2>
                <p className="text-slate-300 mb-8 relative z-10">
                  {(reward.includes('Luck') || reward.includes('Try')) ? 'Better luck next time.' : settings.successMessage}
                </p>

                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 mb-8 relative z-10 w-full">
                  <h4 className="text-sm text-slate-400 uppercase font-semibold mb-1">Your Reward</h4>
                  <div className="text-2xl font-bold text-gradient mb-4">{reward}</div>
                  
                  {!(reward.includes('Luck') || reward.includes('Try')) && (
                    <div className="flex items-center justify-between bg-black/40 rounded-lg p-3 border border-white/5">
                      <code className="text-emerald-400 font-bold tracking-wider">{couponCode}</code>
                      <button onClick={copyToClipboard} className="p-2 hover:bg-white/10 rounded-md transition-colors text-slate-300" title="Copy Code">
                        <SafeIcon icon={FiCopy} />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <button onClick={resetFlow} className="w-full py-4 rounded-xl border border-slate-600 text-slate-300 font-bold text-lg hover:bg-slate-800 transition-all relative z-10">
                Finish & Return
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default FlipRevealFlow;
